import { createContext, useContext, useMemo, useReducer, type ReactNode, type SyntheticEvent } from 'react';
import { Alert, Box, Button, Snackbar, type AlertColor, type SnackbarCloseReason } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import Close from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';

// Success, info and warning toasts auto-hide after this; errors stay until dismissed.
// History: SecuritySettings started at 3000ms (822bbc7, ADM-5902), then the extracted
// components/Toast raised every toast to 300000ms / 5 min (17d8c19, ADM-6528) without a
// recorded reason. That commit also added the error toast with a "contact support" link,
// so the long duration most likely gave users time to click it. Every toast with a link
// is an error, and errors no longer auto-hide, so 6s matches user-management-spa.
export const AUTO_HIDE_DURATION_MS = 6000;

export interface Notification {
  content: ReactNode;
  severity: AlertColor;
}

type Notify = (notification: Notification) => void;

interface QueuedNotification extends Notification {
  key: number;
}

interface NotificationState {
  queue: QueuedNotification[];
  // The head's Snackbar has started entering. Before that it isn't mounted yet:
  // closing it would mount it closed, MUI renders nothing, and onExited never fires.
  isShown: boolean;
  isClosing: boolean;
  nextKey: number;
}

type NotificationAction =
  | { type: 'add'; notification: Notification }
  | { type: 'shown' }
  | { type: 'close' }
  | { type: 'exited' };

// A non-error notification with a newer one behind it is replaced, so it is never shown.
const dropReplaced = (queue: QueuedNotification[]) =>
  queue.filter((notification, index) => notification.severity === 'error' || index === queue.length - 1);

// One Snackbar shows the head of the queue (MUI "Consecutive Snackbars"). A new
// notification closes the current one early, except errors, which stay until the
// user dismisses them.
function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'add': {
      const queue = [...state.queue, { ...action.notification, key: state.nextKey }];
      const [current] = state.queue;
      return {
        ...state,
        queue: state.isShown ? queue : dropReplaced(queue),
        isClosing: state.isClosing || (state.isShown && current.severity !== 'error'),
        nextKey: state.nextKey + 1,
      };
    }
    case 'shown':
      return { ...state, isShown: true };
    case 'close':
      return { ...state, isClosing: true };
    case 'exited':
      return { ...state, queue: dropReplaced(state.queue.slice(1)), isShown: false, isClosing: false };
  }
}

const NotifyContext = createContext<Notify | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [{ queue, isClosing }, dispatch] = useReducer(notificationReducer, {
    queue: [],
    isShown: false,
    isClosing: false,
    nextKey: 0,
  });
  const [current] = queue;

  // Stable across renders, so consumers don't re-render when the queue changes.
  const notify = useMemo<Notify>(() => (notification) => dispatch({ type: 'add', notification }), []);

  const handleClose = (_event?: SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch({ type: 'close' });
  };

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      {current && (
        <Snackbar
          key={current.key}
          open={!isClosing}
          autoHideDuration={current.severity === 'error' ? null : AUTO_HIDE_DURATION_MS}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          // Snackbar forwards only onEnter and onExited from slotProps.transition.
          slotProps={{
            transition: {
              onEnter: () => dispatch({ type: 'shown' }),
              onExited: () => dispatch({ type: 'exited' }),
            },
          }}
        >
          <Alert
            severity={current.severity}
            variant="standard"
            aria-live="polite"
            action={
              <Button
                variant="text"
                size="medium"
                color="inherit"
                aria-label={t('shared.actions.close')}
                onClick={() => handleClose()}
              >
                <Close />
              </Button>
            }
          >
            <Box component="span" style={visuallyHidden}>
              {t(`shared.notification_severity.${current.severity}`)}
            </Box>
            {typeof current.content === 'string' ? <p>{current.content}</p> : current.content}
          </Alert>
        </Snackbar>
      )}
    </NotifyContext.Provider>
  );
}

export function useNotify(): Notify {
  const notify = useContext(NotifyContext);
  if (!notify) {
    throw new Error('useNotify must be used within a NotificationProvider');
  }
  return notify;
}
